import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  getVisibleQuantity,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { ItemDetailActions, RenderActionsFn } from './item-detail-actions';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import {
  ORDER_ITEM_QUANTITY_HEIGHT,
  ZeroQuantityBehavior,
} from '@/components/quantity-selector';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { View } from 'react-native';

interface SharedProps {
  canDecrementToZero?: boolean;
  disabled?: boolean;
  disableTextInput?: boolean;
  inDraggableSheet?: boolean;
  isFocused: boolean;
  quantityInStock?: number;
  renderActions?: RenderActionsFn;
  renderOrderGuideButton?: (variantPackUUID: string) => React.ReactNode;
  showListView?: boolean;
  showTooltipOnMount?: boolean;
  updateQuantity?: UpdateQuantityFn;
  zeroQuantityBehavior?: ZeroQuantityBehavior;
}

type DefaultOrderableItemListItemActionsProps = SharedProps & {
  metadata: ItemListOptionMetadata;
  quantity?: number;
};

type OrderableItemListItemActionsProps = SharedProps & {
  onPress: () => void;
  quantityState?: QuantityStateType;
  skus: ItemListOptionMetadata[];
};

export const DefaultOrderableItemListItemActions: React.FC<
  DefaultOrderableItemListItemActionsProps
> = ({
  canDecrementToZero,
  disabled,
  disableTextInput,
  inDraggableSheet,
  isFocused,
  metadata,
  quantity,
  quantityInStock,
  renderActions,
  renderOrderGuideButton,
  showListView = false,
  showTooltipOnMount,
  updateQuantity,
  zeroQuantityBehavior,
}) => {
  return (
    <ItemDetailActions
      canDecrementToZero={canDecrementToZero}
      disabled={disabled}
      disableTextInput={disableTextInput}
      inDraggableSheet={inDraggableSheet}
      isFocused={isFocused}
      item={metadata}
      quantity={quantity}
      quantityInStock={quantityInStock}
      renderActions={renderActions}
      renderOrderGuideButton={renderOrderGuideButton}
      showInlineModifiers
      showListView={showListView}
      showTooltipOnMount={showTooltipOnMount}
      updateQuantity={updateQuantity}
      zeroQuantityBehavior={zeroQuantityBehavior}
    />
  );
};

interface QuantityMap {
  [variantPackUUID: string]: number;
}

export const OrderableItemListItemActions: React.FC<
  OrderableItemListItemActionsProps
> = ({
  canDecrementToZero,
  disabled,
  disableTextInput,
  inDraggableSheet,
  isFocused,
  onPress,
  quantityInStock,
  quantityState,
  renderActions,
  renderOrderGuideButton,
  showListView = false,
  skus,
  updateQuantity,
  zeroQuantityBehavior,
}) => {
  const [styles] = useStyles();

  const quantityMapping = useMemo(() => {
    if (!quantityState) {
      return {};
    }
    return skus.reduce<QuantityMap>((acc, metadata) => {
      const visibleQuantity = getVisibleQuantity(
        quantityState,
        metadata.variantPackUUID,
      );
      if (visibleQuantity) {
        acc[metadata.variantPackUUID] = visibleQuantity;
      }
      return acc;
    }, {});
  }, [quantityState, skus]);

  if (skus.length === 1) {
    return (
      <DefaultOrderableItemListItemActions
        canDecrementToZero={canDecrementToZero}
        disabled={disabled}
        disableTextInput={disableTextInput}
        inDraggableSheet={inDraggableSheet}
        isFocused={isFocused}
        metadata={skus[0]}
        quantity={
          quantityState &&
          getVisibleQuantity(quantityState, skus[0].variantPackUUID)
        }
        quantityInStock={quantityInStock}
        renderActions={renderActions}
        renderOrderGuideButton={renderOrderGuideButton}
        showListView={showListView}
        updateQuantity={updateQuantity}
        zeroQuantityBehavior={zeroQuantityBehavior}
      />
    );
  }

  const numberAdded = Object.keys(quantityMapping).length;
  if (numberAdded === 1) {
    const addedMetadata = skus.find((s) => quantityMapping[s.variantPackUUID]);
    return (
      <DefaultOrderableItemListItemActions
        canDecrementToZero={canDecrementToZero}
        disabled={disabled}
        disableTextInput={disableTextInput}
        inDraggableSheet={inDraggableSheet}
        isFocused={isFocused}
        metadata={addedMetadata!}
        quantity={
          quantityState &&
          getVisibleQuantity(quantityState, addedMetadata!.variantPackUUID)
        }
        quantityInStock={quantityInStock}
        renderActions={renderActions}
        renderOrderGuideButton={renderOrderGuideButton}
        showListView={showListView}
        updateQuantity={updateQuantity}
        zeroQuantityBehavior={zeroQuantityBehavior}
      />
    );
  }

  return (
    <View style={styles.multiSkuBottomRow}>
      <Button
        containerStyle={styles.buttonContainer}
        onPress={onPress}
        rounded
        size="small"
        style={styles.button}
        textStyle={styles.buttonText}
        trackingData={{
          name:
            numberAdded === 0
              ? 'orderable_item.see_options_button'
              : 'orderable_item.number_added_button',
          properties: {
            sku: skus[0]?.externalID,
          },
        }}
        type="secondary"
      >
        {numberAdded === 0
          ? i18n.t('see_options')
          : i18n.t('number_added', { count: numberAdded })}
      </Button>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  button: {
    borderColor: theme.colors.primary,
    borderWidth: 2,
    height: ORDER_ITEM_QUANTITY_HEIGHT,
  },
  buttonContainer: {
    justifyContent: 'center',
    width: 120,
  },
  buttonText: {
    fontSize: 14,
  },
  multiSkuBottomRow: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
}));
